import { Link, LinkProps } from "react-router-dom";

import {
  EventBadge,
  ShareSocialDropdown,
  SvgSprite,
  useInitialFocus,
} from "@skv/core";
import { resizeImg } from "@skv/util";

interface OpportunityDetailsHeaderProps {
  title: string;
  organisationName?: string;
  organisationMemberName?: string;
  organisationManagingVRC?: string;
  agencyName?: string;
  isOrgLinkedEntity?: boolean;
  imgSrc?: string;
  to: LinkProps["to"];
  eventDate?: string;
  isNew?: boolean;
  focusHeading?: boolean;
}

export const OpportunityDetailsHeader = ({
  title,
  organisationName,
  organisationMemberName,
  organisationManagingVRC,
  agencyName,
  isOrgLinkedEntity,
  imgSrc,
  to,
  eventDate,
  isNew = false,
  focusHeading = false,
}: OpportunityDetailsHeaderProps): JSX.Element => {
  const pageHeadingRef = useInitialFocus<HTMLHeadingElement>({
    shouldFocus: focusHeading,
  });

  return (
    <header className="row mb-4">
      <div className="col-7">
        {isNew && <p className="text-accent mb-2">New</p>}
        {title && (
          <h1 ref={pageHeadingRef} tabIndex={-1} className="h2">
            {title}
          </h1>
        )}
        <p className="h5">
          {organisationMemberName || agencyName ? (
            <>
              {isOrgLinkedEntity === true ? (
                <>
                  <Link to={to}>{organisationName}</Link>{" "}
                  <i className="h6 font-weight-normal">supported by</i>{" "}
                  <span className="h6">{agencyName}</span>
                </>
              ) : (
                <>
                  {organisationMemberName}{" "}
                  <i className="h6 font-weight-normal">managed by</i>{" "}
                  <Link className="h6" to={to}>
                    {organisationName || "View organisation"}
                  </Link>
                </>
              )}
            </>
          ) : (
            <Link
              className="btn btn-forward font-weight-bold"
              style={{
                fontSize: "inherit",
              }}
              to={to}
            >
              {organisationName || "View organisation"}
            </Link>
          )}
        </p>
        {organisationManagingVRC && (
          <small className="d-flex align-items-center gap-1">
            <SvgSprite id="icons:exclamation-circle" width={14} height={14} />
            Advertised by {organisationManagingVRC}
          </small>
        )}
        {eventDate && <EventBadge date={eventDate} />}
      </div>
      <div className="col-4 offset-1 d-flex flex-column justify-content-md-end align-items-end gap-3">
        {imgSrc && (
          <picture>
            <source
              media="(min-width: 1200px)"
              srcSet={[
                `${resizeImg(imgSrc, "120x90")} 1x`,
                `${resizeImg(imgSrc, "240x180")} 2x`,
              ].join(", ")}
            />
            <source
              srcSet={[
                `${resizeImg(imgSrc, "90x90")} 1x`,
                `${resizeImg(imgSrc, "180x180")} 2x`,
                `${resizeImg(imgSrc, "270x270")} 3x`,
              ].join(", ")}
            />
            <img
              src={resizeImg(imgSrc, "120x90")}
              className="img-fluid"
              width={120}
              height={90}
              alt={
                organisationName
                  ? `Logo for ${organisationName}`
                  : "organsation logo"
              }
            />
          </picture>
        )}
        <div>
          <ShareSocialDropdown
            showEmail
            showFacebook
            showLinkedin
            showTwitter
            emailOptions={{
              subject: title,
            }}
          />
        </div>
      </div>
    </header>
  );
};

interface SimplifiedOpportunityDetailsHeaderProps {
  title: string;
  isNew?: boolean;
  imgSrc?: string;
  organisationName?: string;
  focusHeading?: boolean;
}

export const SimplifiedOpportunityDetailsHeader = ({
  title,
  imgSrc,
  organisationName,
  isNew = false,
  focusHeading = false,
}: SimplifiedOpportunityDetailsHeaderProps): JSX.Element => {
  const pageHeadingRef = useInitialFocus<HTMLHeadingElement>({
    shouldFocus: focusHeading,
  });

  return (
    <header className="row mb-3">
      <div className="col-7">
        {isNew && <p className="text-accent mb-2">New</p>}
        {title && (
          <h1 ref={pageHeadingRef} className="h2" tabIndex={-1}>
            {title}
          </h1>
        )}
      </div>
      <div className="col-4 offset-1 d-flex flex-column justify-content-md-end align-items-end gap-3">
        {imgSrc && (
          <picture>
            <source
              media="(min-width: 1200px)"
              srcSet={[
                `${resizeImg(imgSrc, "120x90")} 1x`,
                `${resizeImg(imgSrc, "240x180")} 2x`,
              ].join(", ")}
            />
            <source
              srcSet={[
                `${resizeImg(imgSrc, "90x90")} 1x`,
                `${resizeImg(imgSrc, "180x180")} 2x`,
                `${resizeImg(imgSrc, "270x270")} 3x`,
              ].join(", ")}
            />
            <img
              src={resizeImg(imgSrc, "120x90")}
              className="img-fluid"
              width={120}
              height={90}
              alt={
                organisationName
                  ? `Logo for ${organisationName}`
                  : "organsation logo"
              }
            />
          </picture>
        )}
      </div>
    </header>
  );
};
